import React, { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import classnames from 'classnames/bind';
import { last, shuffle, take, isEmpty } from 'lodash';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { ChatSuggestions, suggestions } from './ChatSuggestions';
import styles from './chatHistory.module.scss';

const classNameBuilder = classnames.bind(styles);

export const chatSender = {
    user: 'user',
    suggestion: 'suggestion',
    ai: 'ai',
};

const INITIAL_CHAT_STATE = [
    {
        sender: chatSender.ai,
        message: (
            <FormattedMessage
                id="landing.pages.form.chat.history.message.1"
                defaultMessage="Welcome to the Landing Page Editor! 👋"
            />
        ),
    },
    {
        sender: chatSender.ai,
        message: (
            <FormattedMessage
                id="landing.pages.form.chat.history.message.2"
                defaultMessage="You can preview your landing page on the right and use this chat to make changes. Just tell me what you'd like to modify, and I'll help you update the content."
            />
        ),
    },
];

export const ChatHistory = ({ lastUserMessage, onSuggestionSubmit }) => {
    const intl = useIntl();
    const chatEndRef = useRef();
    const [chatHistory, setChatHistory] = useState(INITIAL_CHAT_STATE);
    const [visibleSuggestions, setVisibleSuggestions] = useState(() => take(shuffle(suggestions), 2));
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [usedSuggestionIds, setUsedSuggestionIds] = useState(new Set());
    const { value: summary } = useFormFieldValue({ field: 'summary' });

    const unusedSuggestions = useMemo(() => {
        return suggestions.filter(s => !usedSuggestionIds.has(s.id));
    }, [usedSuggestionIds]);

    const addMessage = useCallback((message, sender) => {
        setChatHistory(prevHistory => [...prevHistory, { sender, message }]);
    }, []);

    useEffect(() => {
        if (isEmpty(unusedSuggestions)) {
            setShowSuggestions(false);
            setVisibleSuggestions([]);
        } else {
            const newSuggestions =
                unusedSuggestions.length === 1 ? [unusedSuggestions[0]] : take(shuffle(unusedSuggestions), 2);
            setVisibleSuggestions(newSuggestions);
        }
    }, [unusedSuggestions]);

    // Add user message when lastUserMessage changes
    useEffect(() => {
        if (lastUserMessage) {
            addMessage(lastUserMessage, chatSender.user);
        }
    }, [lastUserMessage, addMessage]);

    // Add AI messages from summary
    useEffect(() => {
        const newMessage = summary && last(summary);
        if (newMessage) {
            addMessage(newMessage, chatSender.ai);
        }
    }, [summary, addMessage]);

    // Auto-scroll to bottom when content changes
    useLayoutEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, showSuggestions, visibleSuggestions]);

    const handleSuggestionClick = useCallback(
        async suggestion => {
            const translatedText = intl.formatMessage({ id: suggestion.id, defaultMessage: suggestion.defaultText });
            addMessage(translatedText, chatSender.suggestion);
            setUsedSuggestionIds(prev => new Set([...prev, suggestion.id]));
            await onSuggestionSubmit(translatedText);
        },
        [addMessage, onSuggestionSubmit, intl]
    );

    const toggleSuggestions = useCallback(() => {
        if (isEmpty(unusedSuggestions)) {
            return; // Don't allow showing suggestions if none are left
        }
        setShowSuggestions(prev => !prev);
        if (!showSuggestions && !isEmpty(unusedSuggestions)) {
            const newSuggestions =
                unusedSuggestions.length === 1 ? [unusedSuggestions[0]] : take(shuffle(unusedSuggestions), 2);
            setVisibleSuggestions(newSuggestions);
        }
    }, [unusedSuggestions, showSuggestions]);

    return (
        <div className={styles['chat-history']}>
            {chatHistory.map(({ message, sender }, index) => (
                <div key={`${sender}-${index}`} className={classNameBuilder('message-container', sender)}>
                    <p className={classNameBuilder('message', sender)}>{message}</p>
                </div>
            ))}
            {!isEmpty(visibleSuggestions) && (
                <ChatSuggestions
                    visibleSuggestions={visibleSuggestions}
                    showSuggestions={showSuggestions}
                    onSuggestionClick={handleSuggestionClick}
                    onToggleSuggestions={toggleSuggestions}
                />
            )}
            <div ref={chatEndRef} />
        </div>
    );
};
